import { requestIdleCallback } from '@theme/utilities';

if (
  typeof window !== 'undefined' &&
  [
    '/pages/faqs',
    '/pages/track-your-order',
    '/pages/shipping-policy',
    '/policies/terms-of-service',
    '/pages/terms-of-service',
    '/policies/refund-policy',
    '/pages/refund-policy',
  ].includes(window.location.pathname)
) {
  import('./everise-policy-pages.js');
}

/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      const template = this.querySelector(':scope > template[shadowrootmode="open"]');

      if (!(template instanceof HTMLTemplateElement)) return;

      const shadow = this.attachShadow({ mode: 'open' });
      shadow.append(template.content.cloneNode(true));
    }
  }
}

/**
 * @typedef {Record<string, Element | Element[] | undefined>} Refs
 */

/**
 * @template {Refs} T
 * @typedef {T & Refs} RefsType
 */

/**
 * Base class that powers our custom web components.
 *
 * Manages references to child elements with `ref` attributes and sets up mutation observers to keep
 * the refs updated when the DOM changes. Also handles declarative event listeners using.
 *
 * @template {Refs} [T=Refs]
 * @extends {DeclarativeShadowElement}
 */
export class Component extends DeclarativeShadowElement {
  /**
   * An object holding references to child elements with `ref` attributes.
   *
   * @type {RefsType<T>}
   */
  refs = /** @type {RefsType<T>} */ ({});

  /**
   * An array of required refs. If a ref is not found, an error will be thrown.
   *
   * @type {string[] | undefined}
   */
  requiredRefs;

  /**
   * Gets the root node of the component, which is either its shadow root or the component itself.
   *
   * @returns {(ShadowRoot | Component<T>)[]} The root nodes.
   */
  get roots() {
    return this.shadowRoot ? [this, this.shadowRoot] : [this];
  }

  /**
   * Called when the element is connected to the document's DOM.
   *
   * Initializes event listeners and refs.
   */
  connectedCallback() {
    super.connectedCallback();
    registerEventListeners();

    this.#updateRefs();

    requestIdleCallback(() => {
      for (const root of this.roots) {
        this.#mutationObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['ref'],
          attributeOldValue: true,
        });
      }
    });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   *
   * Disconnects the mutation observer.
   */
  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  /**
   * Updates the `refs` object by querying all descendant elements with `ref` attributes and storing references to them.
   *
   * This method is called to keep the `refs` object in sync with the DOM.
   */
  #updateRefs() {
    const refs = /** @type any */ ({});
    const elements = this.roots.reduce((acc, root) => {
      for (const element of root.querySelectorAll('[ref]')) {
        if (!this.#isDescendant(element)) continue;
        acc.add(element);
      }

      return acc;
    }, /** @type {Set<Element>} */ (new Set()));

    for (const ref of elements) {
      const refName = ref.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];

        array.push(ref);
        refs[path] = array;
      } else {
        refs[path] = ref;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new MissingRefError(ref, this);
        }
      }
    }

    this.refs = /** @type {RefsType<T>} */ (refs);
  }

  /**
   * MutationObserver instance to observe changes in the component's DOM subtree and update refs accordingly.
   *
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' && [...m.addedNodes, ...m.removedNodes].some(this.#isDescendant))
      )
    ) {
      this.#updateRefs();
    }
  });

  /**
   * Checks if a given node is a descendant of this component.
   *
   * @param {Node} node - The node to check.
   * @returns {boolean} True if the node is a descendant of this component.
   */
  #isDescendant = (node) => getClosestComponent(getAncestor(node)) === this;
}

/**
 * Get the ancestor of a given node.
 *
 * @param {Node} node - The node to get the ancestor of.
 * @returns {Node | null} The ancestor of the node or null if none is found.
 */
function getAncestor(node) {
  if (node.parentNode) return node.parentNode;

  const root = node.getRootNode();
  if (root instanceof ShadowRoot) return root.host;

  return null;
}

/**
 * Recursively finds the closest ancestor that is an instance of `Component`.
 *
 * @param {Node | null} node - The starting node to search from.
 * @returns {HTMLElement | null} The closest ancestor `Component` instance or null if none is found.
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;

  const ancestor = getAncestor(node);
  if (ancestor) return getClosestComponent(ancestor);

  return null;
}

/**
 * Initializes the event listeners for custom event handling.
 *
 * Sets up event listeners for specified events and delegates the handling of those events
 * to methods defined on the closest `Component` instance, based on custom attributes.
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = ['click', 'change', 'select', 'focus', 'blur', 'submit', 'input', 'keydown', 'keyup', 'toggle'];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);

        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
                get(target, property) {
                  if (property === 'target') return element;

                  const value = Reflect.get(target, property);

                  if (typeof value === 'function') {
                    return value.bind(target);
                  }

                  return value;
                },
              })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');
        // Extract the last segment of the attribute value delimited by `?` or `/`
        // Do not use lookback for Safari 16.0 compatibility
        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;
        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            /** @type {(Event | Data)[]} */
            const args = [proxiedEvent];

            if (data) args.unshift(parseData(data));

            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;

    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) {
      return target;
    }

    if (expensiveEvents.includes(event.type)) {
      return null;
    }

    return event.bubbles || shouldBubble.includes(event.type) ? target.closest(`[on\\:${event.type}]`) : null;
  }
}

/**
 * Parses a string to extract data based on a delimiter.
 *
 * @param {string} str - The string to parse.
 * @returns {Object|Array<string|number>|string} The parsed data.
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
        Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
      )
    : parseValue(data);
}

/**
 * @typedef {Object|Array<string|number>|string} Data
 */

/**
 * Parses a string value to its appropriate type.
 *
 * @param {string} str - The string to parse.
 * @returns {Data} The parsed value.
 */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;

  const maybeNumber = Number(str);
  if (!isNaN(maybeNumber) && str.trim() !== '') return maybeNumber;

  return str;
}

/**
 * Throws a formatted error when a required ref is not found in the component.
 */
class MissingRefError extends Error {
  /**
   * @param {string} ref
   * @param {Component} component
   */
  constructor(ref, component) {
    super(`Required ref "${ref}" not found in component ${component.tagName.toLowerCase()}`);
  }
}

/**
 * Temporary compatibility bridge for cached Everise manage-appointment pages.
 *
 * Shopify can serve a cached product alternate template for a short period after
 * the section has been updated. Older cached markup used window.confirm() for
 * appointment cancellation. This bridge intercepts only that exact confirmation
 * on the Everise manage appointment page and replaces it with a branded modal.
 */
(() => {
  const CANCEL_MESSAGE = 'Cancel this Everise showroom appointment?';
  const MANAGE_VIEWS = new Set(['manage-appointment', 'manage-modal']);
  const CANCEL_URL = 'https://n8n.srv823907.hstgr.cloud/webhook/everise-manage-appointment-cancel';

  const originalConfirm = window.confirm.bind(window);

  function isEveriseManagePage() {
    const params = new URLSearchParams(window.location.search);
    return MANAGE_VIEWS.has(params.get('view')) && Boolean(document.querySelector('[data-everise-manage-appointment]'));
  }

  function readToken() {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const query = new URLSearchParams(window.location.search);
    return hash.get('token') || query.get('token') || '';
  }

  function setText(selector, value) {
    const element = document.querySelector(selector);
    if (element) element.textContent = value || '';
  }

  function showState(name) {
    document.querySelectorAll('[data-everise-manage-appointment] [data-state]').forEach((panel) => {
      const active = panel.dataset.state === name;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
  }

  function appointmentSummary() {
    const date = document.querySelector('[data-detail-date]')?.textContent?.trim() || 'Everise showroom fitting';
    const time = document.querySelector('[data-detail-time]')?.textContent?.trim() || '';
    return { date, time };
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function injectStyles() {
    if (document.getElementById('everise-cancel-bridge-style')) return;
    const style = document.createElement('style');
    style.id = 'everise-cancel-bridge-style';
    style.textContent = `
      .everise-cancel-bridge[hidden] { display: none; }
      .everise-cancel-bridge {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        display: grid;
        place-items: center;
        padding: 22px;
      }
      .everise-cancel-bridge__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(39, 27, 21, 0.54);
      }
      .everise-cancel-bridge__dialog {
        position: relative;
        width: min(100%, 560px);
        display: grid;
        gap: 16px;
        padding: clamp(22px, 4vw, 34px);
        border: 1px solid rgba(185, 139, 75, 0.26);
        border-radius: 28px;
        background: radial-gradient(circle at 100% 0%, rgba(244, 224, 198, 0.72), rgba(255, 250, 243, 0) 42%), #fffaf3;
        color: #271b15;
        box-shadow: 0 34px 90px rgba(39, 27, 21, 0.28);
        outline: none;
      }
      .everise-cancel-bridge__kicker {
        margin: 0;
        color: #9d6d35;
        font-size: 0.72rem;
        line-height: 1.2;
        letter-spacing: 0.18em;
        text-transform: uppercase;
        font-weight: 850;
      }
      .everise-cancel-bridge__dialog h2 {
        margin: 0;
        font-family: var(--font-heading--family, serif);
        font-size: clamp(2.4rem, 8vw, 3.6rem);
        line-height: 0.95;
        letter-spacing: 0;
      }
      .everise-cancel-bridge__dialog p {
        margin: 0;
        color: #554237;
        font-size: 1rem;
        line-height: 1.6;
      }
      .everise-cancel-bridge__summary {
        display: grid;
        gap: 4px;
        padding: 16px 18px;
        border: 1px solid rgba(185, 139, 75, 0.2);
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.62);
      }
      .everise-cancel-bridge__summary span {
        color: #8a633d;
        font-size: 0.74rem;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .everise-cancel-bridge__summary strong {
        color: #271b15;
        font-size: 1.05rem;
        line-height: 1.35;
      }
      .everise-cancel-bridge__actions {
        display: grid;
        gap: 10px;
      }
      .everise-cancel-bridge button {
        min-height: 54px;
        border-radius: 18px;
        padding: 0 20px;
        font-weight: 850;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        cursor: pointer;
      }
      .everise-cancel-bridge__keep {
        border: 1px solid rgba(185, 139, 75, 0.26);
        background: rgba(255, 255, 255, 0.72);
        color: #271b15;
      }
      .everise-cancel-bridge__confirm {
        border: 1px solid rgba(159, 63, 36, 0.24);
        background: #9f3f24;
        color: #fffaf3;
      }
      .everise-cancel-bridge__confirm:disabled {
        cursor: wait;
        opacity: 0.72;
      }
      @media (min-width: 760px) {
        .everise-cancel-bridge__actions { grid-template-columns: 1fr 1fr; }
      }
    `;
    document.head.appendChild(style);
  }

  function closeModal(modal) {
    modal.hidden = true;
    modal.remove();
  }

  async function cancelAppointment(modal, confirmButton) {
    const token = readToken();
    if (!token) {
      setText('[data-cancel-message]', 'This appointment link is missing its secure token.');
      closeModal(modal);
      return;
    }

    confirmButton.disabled = true;
    confirmButton.textContent = 'Cancelling...';

    try {
      const response = await fetch(CANCEL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify({ token })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.error || 'The appointment could not be cancelled.');
      }
      closeModal(modal);
      setText('[data-success-title]', 'Appointment cancelled');
      setText('[data-success-message]', 'Your cancellation has been confirmed by email.');
      showState('success');
    } catch (error) {
      setText('[data-cancel-message]', error.message || 'The appointment could not be cancelled.');
      closeModal(modal);
    }
  }

  function showCancelModal() {
    injectStyles();
    const existing = document.querySelector('[data-everise-cancel-bridge]');
    if (existing) existing.remove();

    const summary = appointmentSummary();
    const modal = document.createElement('div');
    modal.className = 'everise-cancel-bridge';
    modal.setAttribute('data-everise-cancel-bridge', '');
    modal.innerHTML = `
      <div class="everise-cancel-bridge__backdrop" data-everise-cancel-close></div>
      <div class="everise-cancel-bridge__dialog" role="dialog" aria-modal="true" aria-labelledby="everise-cancel-bridge-title" tabindex="-1">
        <p class="everise-cancel-bridge__kicker">Please confirm</p>
        <h2 id="everise-cancel-bridge-title">Cancel this appointment?</h2>
        <p>This will remove your showroom fitting from the Everise calendar and send a cancellation confirmation by email.</p>
        <div class="everise-cancel-bridge__summary" aria-label="Appointment being cancelled">
          <span>${escapeHtml(summary.date)}</span>
          <strong>${escapeHtml(summary.time)}</strong>
        </div>
        <div class="everise-cancel-bridge__actions">
          <button class="everise-cancel-bridge__keep" type="button" data-everise-cancel-close>Keep appointment</button>
          <button class="everise-cancel-bridge__confirm" type="button" data-everise-cancel-confirm>Yes, cancel appointment</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    const dialog = modal.querySelector('.everise-cancel-bridge__dialog');
    const confirmButton = modal.querySelector('[data-everise-cancel-confirm]');
    window.requestAnimationFrame(() => dialog?.focus());

    modal.addEventListener('click', (event) => {
      if (event.target.closest('[data-everise-cancel-close]')) closeModal(modal);
      if (event.target.closest('[data-everise-cancel-confirm]') && confirmButton) {
        cancelAppointment(modal, confirmButton);
      }
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal(modal);
    });
  }

  window.confirm = (message) => {
    if (message === CANCEL_MESSAGE && isEveriseManagePage()) {
      showCancelModal();
      return false;
    }
    return originalConfirm(message);
  };
})();
